import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import { LoadingSkeleton4 } from "@/components/Skeletons";
import { Input } from "@/components/Inputs";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { cardsImage, googlepayImage } from "@/constants";
import { toast } from "react-toastify";
import axios from "axios";

export default () => {
    const navSections = [
        { id: 0, name: "Contact Information", component: <ContactSection /> },
        { id: 1, name: "Change Password", component: <ChangePassword /> },
        { id: 2, name: "Payment Details", component: <PaymentSecttion /> },
        { id: 3, name: "Logout", component: <LogoutSection /> },
    ];
    const [activeNav, setActiveNav] = useState(navSections[0]);

    return (
        <main className="relative w-full">
            <div className="pb-6 md:pb-12 flex flex-col md:flex-row justify-center items-start padding-x">
                <section className="sm:max-h-[90vh] flex flex-col justify-between bg-white rounded-lg relative md:sticky md:top-8 z-0 w-full md:w-1/3 md:mr-5 mb-6 md:mt-0">
                    <nav className="overflow-y-scroll w-full">
                        {navSections.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <div
                                    className={`${
                                        activeNav.id === item.id
                                            ? "border-primary"
                                            : "hover:border-shade border-transparent"
                                    } cursor-pointer px-3 py-5 border-l-[6px] rounded-lg transition-all`}
                                    onClick={() => setActiveNav(item)}
                                >
                                    <h1 className="truncate">{item.name}</h1>
                                </div>
                                {index + 1 !== navSections.length ? <hr className="mx-3 border-[1px]" /> : null}
                            </React.Fragment>
                        ))}
                    </nav>
                </section>
                <section className="w-full md:w-2/3 md:ml-5">{activeNav.component}</section>
            </div>
        </main>
    );
};

const ContactSection = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [fname, setFname] = useState("");
    const [lname, setLname] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const { user } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const { data } = await axios.get(
                    process.env.REACT_APP_API_URL + "/CustomerAccounts/GetCustomerAccount?CustomerId=" + user.data.userId
                );
                setFname(data.data[0].firstName);
                setLname(data.data[0].lastName);
                setPhone(data.data[0].phone);
                setEmail(data.data[0].email);
                setAddress(data.data[0].address);
                setCity(data.data[0].city);
                setCountry(data.data[0].country);
                setPostalCode(data.data[0].postalCode);
            } catch (error) {
                setIsError(true);
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const onUpdate = async (e) => {
        if (!fname) return toast.error("First name is required");
        if (!lname) return toast.error("Last name is required");
        if (!phone) return toast.error("Mobile Number is required");
        if (!email) return toast.error("Email is required");
        e.preventDefault();
        try {
            const response = await axios.put(
                process.env.REACT_APP_API_URL + "/CustomerAccounts/CustomerAccountUpdate",
                {
                    customerID: user.data.userId,
                    firstName: fname,
                    lastName: lname,
                    email: email,
                    phone: phone,
                    address: address,
                    city: city,
                    postalCode: postalCode,
                    country: country,
                }
            );
            toast.success("Profile Updated");
        } catch (error) {
            console.log("error occured");
        }
    };

    let content;
    if (isLoading) content = <LoadingSkeleton4 />;
    else if (isError)
        content = (
            <div className="w-full bg-white rounded-lg text-shade-dark">
                <h2 className="text-center p-2">Error Loading User Data</h2>
            </div>
        );
    else
        content = (
            <div className="p-2 bg-white rounded-lg">
                <h2 className="text-xl p-2 pb-4">Contact Information</h2>
                <form onSubmit={onUpdate} className="flex flex-wrap">
                    <Input
                        value={fname}
                        onChange={(e) => setFname(e.target.value)}
                        width="w-1/2"
                        type="text"
                        placeholder="First name"
                    />
                    <Input
                        value={lname}
                        onChange={(e) => setLname(e.target.value)}
                        width="w-1/2"
                        type="text"
                        placeholder="Last name"
                    />
                    <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        type="number"
                        placeholder="Mobile number"
                    />
                    <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="Email address"
                    />
                    <Input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        type="address"
                        placeholder="Address (optional)"
                    />
                    <Input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        type="city"
                        placeholder="City (optional)"
                    />
                    <Input
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        width="w-1/2"
                        type="country"
                        placeholder="Country (optional)"
                    />
                    <Input
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        width="w-1/2"
                        type="postalCode"
                        placeholder="PostalCode (optional)"
                    />
                    <button
                        type="submit"
                        className="transition-colors bg-secondary hover:bg-primary hover:text-secondary text-shade-light rounded w-full h-12 text-sm px-6 m-2"
                    >
                        Update
                    </button>
                </form>
            </div>
        );

    return content;
};

const ChangePassword = () => {
    const initialState = { current: "", new: "", confirm: "" };
    const [passwords, setPasswords] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();

    const onChangePassword = async (e) => {
        e.preventDefault();
        if (!passwords.current || !passwords.new || !passwords.confirm)
            return toast.error("Please fill in all the fields");
        if (passwords.new !== passwords.confirm) return toast.error("Passwords do not match");
        if (passwords.current === passwords.new) return toast.error("Use a different password");
        try {
            setIsLoading(true);
            const queryString = new URLSearchParams({
                CustomerID: user.data.userId,
                Email: user.data.email,
                CurrentPassword: passwords.current,
                NewPassword: passwords.new,
            }).toString();

            const response = await axios.put(
                process.env.REACT_APP_API_URL + "/CustomerAccounts/CustomerAccountUpdatePassword?" + queryString
            );
            if (response.data.code >= 200 && response.data.code <= 299) {
                setPasswords(initialState);
                toast.success("Password Changed");
            } else {
                toast.error(response.data.description || "An error occurred");
            }
        } catch (error) {
            console.log(error);
            console.log(error?.response?.statusText || "error occured");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-2 bg-white rounded-lg">
            <h2 className="text-xl p-2 pb-4">Change Password</h2>
            <form onSubmit={onChangePassword} className="flex flex-wrap">
                <Input
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    type="password"
                    placeholder="Current Password"
                />
                <Input
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    type="password"
                    placeholder="New Password"
                />
                <Input
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    type="password"
                    placeholder="Confirm Password"
                />
                <button
                    type="submit"
                    className="transition-colors bg-secondary hover:bg-primary hover:text-secondary text-shade-light rounded w-full h-12 text-sm px-6 m-2"
                >
                    Change
                </button>
            </form>
        </div>
    );
};

const PaymentSecttion = () => {
    const data = [{ id: 0 }, { id: 1 }];
    return (
        <>
            <div className="p-2 bg-white rounded-lg">
                <h2 className="text-xl p-2 pb-6">Payment Details</h2>
                <div className="w-full">
                    {data.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <hr className="mx-2 border-[1px]" />
                            <div className={`flex items-center justify-between cursor-pointer px-2 py-3`}>
                                <div className="flex gap-3 items-center">
                                    {index < 1 ? (
                                        <img className="w-8" src={`../${cardsImage}`} alt="cards image" />
                                    ) : (
                                        <div className="bg-white border border-black rounded-lg p-1">
                                            <img
                                                className="object-cover object-center h-4 w-8"
                                                src={`../${googlepayImage}`}
                                                alt="cards image"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <h2 className="text-xs text-shade-dark truncate">Debit / Credit Card</h2>
                                        <h1 className="text-lg truncate">**** **** **** 1234</h1>
                                    </div>
                                </div>
                                <ChevronDownIcon className="w-5 h-5 text-primary" />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>
            <button className="transition-colors bg-secondary hover:bg-primary hover:text-secondary text-shade-light rounded w-full h-12 text-sm px-6 mt-6">
                Add Payment
            </button>
        </>
    );
};

const LogoutSection = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const onLogout = () => {
        logout();
        navigate("/");
        toast.success("logout successfull");
    };

    return (
        <>
            <div className="p-2 bg-white rounded-lg">
                <h2 className="text-xl p-2">Confirm Logout</h2>
                <div className="w-full p-2">
                    <button
                        onClick={onLogout}
                        className="transition-colors bg-secondary hover:bg-red-700 text-white rounded w-full h-12 text-sm px-6 mt-2"
                    >
                        Logout
                    </button>
                </div>
            </div>
            ;
        </>
    );
};
